import React, { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp, ChevronsUpDown, Search } from 'lucide-react';
import { EmptyState } from './EmptyState';

export interface Column<T> {
  key: string;
  header: string;
  render?: (row: T, index: number) => React.ReactNode;
  sortable?: boolean;
  align?: 'left' | 'center' | 'right';
  className?: string;
  sortValue?: (row: T) => string | number | null | undefined;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  keyExtractor: (row: T, index: number) => string | number;
  searchKey?: (row: T) => string;
  searchPlaceholder?: string;
  pageSize?: number;
  emptyTitle?: string;
  emptyDescription?: string;
  className?: string;
  onRowClick?: (row: T) => void;
  caption?: string;
}

export function DataTable<T>({
  data,
  columns,
  keyExtractor,
  searchKey,
  searchPlaceholder = 'Search records...',
  pageSize = 10,
  emptyTitle = 'No records found',
  emptyDescription = 'There is currently no data to display.',
  className,
  onRowClick,
  caption,
}: DataTableProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);

  // Filter
  const filteredData = useMemo(() => {
    if (!searchTerm || !searchKey) return data;
    const term = searchTerm.toLowerCase().trim();
    return data.filter((row) => searchKey(row).toLowerCase().includes(term));
  }, [data, searchTerm, searchKey]);

  // Sort
  const sortedData = useMemo(() => {
    if (!sortKey) return filteredData;
    const col = columns.find((c) => c.key === sortKey);
    if (!col) return filteredData;

    return [...filteredData].sort((a, b) => {
      let valA: string | number | null | undefined;
      let valB: string | number | null | undefined;

      if (col.sortValue) {
        valA = col.sortValue(a);
        valB = col.sortValue(b);
      } else {
        const rawA = (a as Record<string, unknown>)[sortKey];
        const rawB = (b as Record<string, unknown>)[sortKey];
        valA = typeof rawA === 'number' || typeof rawA === 'string' ? rawA : String(rawA ?? '');
        valB = typeof rawB === 'number' || typeof rawB === 'string' ? rawB : String(rawB ?? '');
      }

      if (valA === null || valA === undefined) valA = '';
      if (valB === null || valB === undefined) valB = '';

      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortKey, sortOrder, columns]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      if (sortOrder === 'asc') setSortOrder('desc');
      else {
        setSortKey(null);
        setSortOrder('desc');
      }
    } else {
      setSortKey(key);
      setSortOrder('desc');
    }
  };

  return (
    <div className={cn('space-y-3', className)}>
      {/* Search and summary bar */}
      {searchKey && (
        <div className="flex items-center justify-between gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-dim)]" aria-hidden="true" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className="w-full pl-9 pr-3 py-1.5 text-xs font-mono bg-[var(--bg-input)] border border-[var(--border-default)] rounded-md text-[var(--text-primary)] placeholder-[var(--text-dim)] focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] focus-visible:outline-none"
            />
          </div>
          <div className="text-xs font-mono text-[var(--text-muted)] tabular-nums" aria-live="polite">
            Showing {Math.min(sortedData.length, (currentPage - 1) * pageSize + 1)}-
            {Math.min(sortedData.length, currentPage * pageSize)} of {sortedData.length} entries
          </div>
        </div>
      )}

      {/* Table Container */}
      <div className="overflow-x-auto rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)]">
        <table className="w-full text-left text-xs font-sans">
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead className="bg-[var(--bg-surface-elevated)] border-b border-[var(--border-default)] text-[var(--text-muted)] font-mono uppercase tracking-wider text-[11px]">
            <tr>
              {columns.map((col) => {
                const isSorted = sortKey === col.key;
                const sortAria = isSorted
                  ? sortOrder === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : undefined;

                return (
                  <th
                    key={col.key}
                    scope="col"
                    aria-sort={sortAria}
                    onClick={() => col.sortable && handleSort(col.key)}
                    onKeyDown={(e) => {
                      if (col.sortable && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        handleSort(col.key);
                      }
                    }}
                    tabIndex={col.sortable ? 0 : undefined}
                    role={col.sortable ? 'button' : undefined}
                    className={cn(
                      'px-4 py-3 font-semibold select-none',
                      col.sortable && 'cursor-pointer hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-muted)] focus-visible:bg-[var(--bg-surface-muted)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--focus-ring)]',
                      col.align === 'center' && 'text-center',
                      col.align === 'right' && 'text-right',
                      col.className
                    )}
                  >
                    <div
                      className={cn(
                        'inline-flex items-center gap-1.5',
                        col.align === 'right' && 'justify-end w-full',
                        col.align === 'center' && 'justify-center w-full'
                      )}
                    >
                      <span>{col.header}</span>
                      {col.sortable && (
                        <span className="text-[var(--text-dim)]" aria-hidden="true">
                          {isSorted ? (
                            sortOrder === 'asc' ? (
                              <ChevronUp className="w-3.5 h-3.5 text-[var(--accent-primary)]" />
                            ) : (
                              <ChevronDown className="w-3.5 h-3.5 text-[var(--accent-primary)]" />
                            )
                          ) : (
                            <ChevronsUpDown className="w-3 h-3 opacity-40" />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border-subtle)] font-mono">
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-12">
                  <EmptyState title={emptyTitle} description={emptyDescription} />
                </td>
              </tr>
            ) : (
              paginatedData.map((row, idx) => (
                <tr
                  key={keyExtractor(row, idx)}
                  onClick={() => onRowClick && onRowClick(row)}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      onRowClick(row);
                    }
                  }}
                  className={cn(
                    'transition-colors duration-150 ease-out hover:bg-[var(--bg-surface-elevated)]',
                    onRowClick && 'cursor-pointer focus-visible:bg-[var(--bg-surface-elevated)] focus-visible:outline-none'
                  )}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn(
                        'px-4 py-3 text-[var(--text-secondary)] text-xs',
                        col.align === 'center' && 'text-center',
                        col.align === 'right' && 'text-right',
                        col.className
                      )}
                    >
                      {col.render
                        ? col.render(row, idx)
                        : (row as Record<string, unknown>)[col.key] !== undefined &&
                          (row as Record<string, unknown>)[col.key] !== null
                        ? String((row as Record<string, unknown>)[col.key])
                        : '—'}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <div className="text-xs font-mono text-[var(--text-muted)]">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              aria-label="Previous Page"
              className="px-2.5 py-1 text-xs font-mono rounded bg-[var(--bg-surface-elevated)] border border-[var(--border-default)] text-[var(--text-secondary)] disabled:opacity-30 hover:bg-[var(--bg-surface-muted)] hover:text-[var(--text-primary)] active:scale-95 disabled:hover:bg-[var(--bg-surface-elevated)] transition-all duration-150 focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              aria-label="Next Page"
              className="px-2.5 py-1 text-xs font-mono rounded bg-[var(--bg-surface-elevated)] border border-[var(--border-default)] text-[var(--text-secondary)] disabled:opacity-30 hover:bg-[var(--bg-surface-muted)] hover:text-[var(--text-primary)] active:scale-95 disabled:hover:bg-[var(--bg-surface-elevated)] transition-all duration-150 focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
